import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../App";
import { ArrowLeft, FileText, Mail, Briefcase } from "lucide-react";

export default function TeamProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/team/${userId}`).then(r => setData(r.data)).catch(e => { console.error(e); navigate("/me"); }).finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] font-heading text-xl">LOADING...</div></div>;
  if (!data) return null;

  const u = data.user || {};
  const briefs = data.assigned_briefs || [];
  const tasks = data.assigned_tasks || [];
  const projects = data.projects || [];

  return (
    <>
      
      <div>
        <div className="max-w-3xl mx-auto space-y-5" data-testid="team-profile">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 text-[var(--t3)] hover:text-[var(--t1)]"><ArrowLeft size={20} /></button>
            <div>
              <h1 className="font-heading text-2xl text-[var(--t1)]">{u.full_name || u.name || "Team Member"}</h1>
              <p className="text-[var(--t3)] text-sm normal-case">{u.designation || u.role || ""}</p>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-5 flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[var(--s3)] flex items-center justify-center text-white font-bold text-lg">
              {(u.full_name || u.name || "?")[0]?.toUpperCase()}
            </div>
            <div>
              <p className="text-[var(--t1)] text-base font-medium normal-case">{u.full_name || u.name}</p>
              <p className="text-[var(--t3)] text-sm normal-case">{u.designation || ""}</p>
              <div className="flex items-center gap-3 mt-1 text-xs text-[var(--t3)]">
                <span className="flex items-center gap-1"><Mail size={10} />{u.email}</span>
                <span className="flex items-center gap-1"><Briefcase size={10} />{u.role}</span>
              </div>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-5">
            <h2 className="text-[var(--t3)] text-[13px] tracking-[0.2em] mb-4">BRIEFS ({briefs.length})</h2>
            {briefs.length === 0 ? (
              <p className="text-[var(--t4)] text-xs text-center py-8 normal-case font-normal">No briefs</p>
            ) : (
              <div className="space-y-2">
                {briefs.map(b => (
                  <button key={b.brief_id} onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}
                    className="w-full text-left flex items-center justify-between p-3 rounded-xl hover:bg-[var(--s2)] transition-colors">
                    <div>
                      <p className="text-[var(--t1)] text-sm normal-case">{b.title}</p>
                      <p className="text-[var(--t3)] text-xs normal-case">
                        {b.company_name} | {b.status}
                        {b.member_role === "reviewer" && <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--s2)] text-[10px] text-[var(--c)]">Reviewer</span>}
                      </p>
                    </div>
                    {b.pitch_date && <span className="text-[#E31937] text-[13px]">{b.pitch_date}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Tasks */}
          <div className="glass-card p-5">
            <h2 className="text-[var(--t3)] text-xs tracking-widest uppercase mb-4 font-medium">TASKS ({tasks.length})</h2>
            {tasks.length === 0 ? (
              <p className="text-[var(--t4)] text-xs text-center py-6">No tasks</p>
            ) : (
              <div className="space-y-2">
                {tasks.map(t => (
                  <button key={t.task_id} onClick={() => navigate(`/tasks/${t.task_id}`)} className="w-full text-left flex items-center justify-between p-3 rounded-lg hover:bg-[var(--s1)] transition-colors">
                    <div>
                      <p className="text-[var(--t1)] text-sm">{t.title}</p>
                      <p className="text-[var(--t3)] text-xs">
                        {t.status} | {t.priority}
                        {t.member_role === "reviewer" && <span className="ml-2 px-1.5 py-0.5 rounded bg-[var(--s2)] text-[10px] text-[var(--c)]">Reviewer</span>}
                      </p>
                    </div>
                    {t.due_date && <span className="text-[var(--t3)] text-xs">{t.due_date}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Projects */}
          <div className="glass-card p-5">
            <h2 className="text-[var(--t3)] text-xs tracking-widest uppercase mb-4 font-medium">PROJECTS ({projects.length})</h2>
            {projects.length === 0 ? (
              <p className="text-[var(--t4)] text-xs text-center py-6">No projects</p>
            ) : (
              <div className="space-y-2">
                {projects.map(p => (
                  <button key={p.project_id} onClick={() => navigate(`/projects/${p.project_id}`)} className="w-full text-left flex items-center justify-between p-3 rounded-lg hover:bg-[var(--s1)] transition-colors">
                    <p className="text-[var(--t1)] text-sm">{p.name || p.title}</p>
                    <span className="text-[var(--t3)] text-xs">{p.status}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
